import { faCopy } from '@fortawesome/pro-solid-svg-icons/faCopy';
import { createFileRoute } from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { type FC, useCallback, useContext, useState } from 'react';
import { Card, Col, Row, Spinner } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';

import { makePortalUserNotificationsChannel, SocketEvent } from '@furality/centrifugo-types';
import {
  IgnoredError,
  ReportError,
  useAuth,
  useMutation,
  useQuery,
} from '@furality/frontend-stack';
import { Button } from '@furality/furality-purpose';
import fox from '#/api/fox.ts';
import FontAwesomeIcon from '#/components/FontAwesomeIcon.tsx';
import useTimedState from '#/hooks/useTimedState.ts';
import { FriendLocationContext } from '@/components/__app/FriendLocationProvider.tsx';
import JoinPartyModal from '@/components/_party/JoinPartyModal.tsx';
import LeavePartyModal from '@/components/_party/LeavePartyModal.tsx';
import Member from '@/components/_party/Member.tsx';
import { FullLayout } from '@/components/layouts/index.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import PageDisabled from '@/components/PageDisabled.tsx';
import RefreshButton from '@/components/RefreshButton.tsx';
import copyToClipboard from '@/lib/helpers/copyToClipboard.ts';
import useChannelEventSubscription from '@/lib/hooks/useChannelEventSubscription';
import useFlag from '@/lib/hooks/useFlag.ts';
import QUERY_KEYS from '@/lib/QUERY_KEYS.ts';
import useToasts from '@/lib/useToast.ts';

const MAX_PARTY_MEMBERS = 10;

const PartyViewInternal: FC = () => {
  const auth = useAuth();
  const { pushToast } = useToasts();

  const featuresReady = useFlagsStatus().flagsReady;
  const enableParty = useFlag('party');

  const { t } = useTranslation(['common', 'party']);

  const [pending, setPending] = useState(false);
  const [isParty, setIsParty] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  const [showJoinPartyModal, setShowJoinPartyModal] = useState(false);
  const [showLeavePartyModal, setShowLeavePartyModal] = useState(false);

  const [copiedToClipboard, setCopiedToClipboard] = useTimedState(false, 5000);

  const { useActiveFriends } = useContext(FriendLocationContext);
  const activeFriends = useActiveFriends();

  const reset = () => {
    setIsParty(false);
    setIsOwner(false);
    setPending(false);
  };

  const party = useQuery({
    queryKey: [QUERY_KEYS.Party.Party],
    queryFn: async () => {
      const response = await fox.value.partyFind();
      switch (response.status) {
        case 200: {
          const me = response.data.members.find(
            (member) => member.vrchatId === auth.user?.vrchatId,
          );
          if (me) {
            setIsParty(true);
            setPending(!me.accepted);
            setIsOwner(me.isOwner);
          } else {
            // this should never happen since you should always be in your own party.
            reset();
            console.error('Could not find self in party members.');
            pushToast('error', t('party:party.cant_get_party_data'));
          }
          return response.data;
        }

        case 404:
          reset();
          throw new IgnoredError(response);

        default:
          pushToast('error', t('party:party.cant_get_party_data'));
          throw new ReportError(response);
      }
    },
    enabled: enableParty,
  });

  const createParty = useMutation({
    mutationFn: async () => {
      const response = await fox.value.partyCreate();
      switch (response.status) {
        case 201:
          await party.refetch();
          pushToast('info', t('party:party.party_created'));
          return 'ok';
        default:
          pushToast('error', t('party:party.errors.cant_create_party'));
          throw new ReportError(response);
      }
    },
  });

  const toClipboard = useMutation({
    mutationFn: (content: string) =>
      copyToClipboard(content)
        .then(() => setCopiedToClipboard(true))
        .catch((): undefined => {
          pushToast('error', t('party:party.failed_to_clipboard'));
        }),
  });

  const messageCallback = useCallback(() => {
    void party.refetch();
  }, [party]);

  const channel = makePortalUserNotificationsChannel(auth.user?.id ?? '');
  useChannelEventSubscription(channel, SocketEvent.UserJoinedParty, messageCallback, {
    enabled: !!auth.user?.id,
  });
  useChannelEventSubscription(channel, SocketEvent.UserLeftParty, messageCallback, {
    enabled: !!auth.user?.id,
  });
  useChannelEventSubscription(channel, SocketEvent.JoinedParty, messageCallback, {
    enabled: !!auth.user?.id,
  });

  const partyCode = party.data?.partyId;

  if (!featuresReady || activeFriends === undefined) {
    return <PageLoading />;
  }

  if (!enableParty) {
    return <PageDisabled />;
  }

  return (
    <div className="mt-4 mt-xl-6 mb-md-4">
      <div className="d-flex align-items-center">
        <h1 className="text-white mb-0">{t('shared.titles.party')}</h1>
        {isParty && (
          <RefreshButton
            className="ms-2 ms-md-4 flex-shrink-0"
            onClick={() => party.refetch()}
            title={t('party:party.refresh')}
            variant="dark"
          />
        )}
      </div>
      <p className="text-muted mt-4 mb-0">
        <Trans
          ns="party"
          i18nKey="party.introduction"
          components={{
            b: <b className="text-info"></b>,
          }}
          values={{
            maxPartyMembers: MAX_PARTY_MEMBERS,
          }}
        />
      </p>
      {party.isLoading && (
        <Spinner
          animation="border"
          role="status"
          aria-hidden="true"
          className="mt-4 mb-1 mx-auto d-block"
        />
      )}
      {!isParty && !party.isLoading && (
        <>
          <div className="mt-4">
            <Button
              variant="primary"
              className="mb-3"
              onClick={() => createParty.mutate()}
              disabled={createParty.isPending || party.isLoading}
            >
              {t('party:party.create_party')}
            </Button>
            <Button
              variant="primary"
              className="ml-md-2 mb-3"
              onClick={() => setShowJoinPartyModal(true)}
              disabled={createParty.isPending || party.isLoading}
            >
              {t('party:party.join_a_party')}
            </Button>
          </div>
          <JoinPartyModal
            show={showJoinPartyModal}
            onClose={() => setShowJoinPartyModal(false)}
            onSuccess={() => party.refetch()}
          />
        </>
      )}
      {pending && (
        <>
          <div className="table-responsive">
            <table className="table table-cards align-items-center">
              <thead>
                <tr>
                  <th scope="col" className="sort" />
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <span className="h6 text-sm font-weight-bold mb-0">
                      {t('party:party.pending_approval')}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-5 text-center">
            <Button
              size="sm"
              className="mb-3"
              variant="outline-danger"
              onClick={() => setShowLeavePartyModal(true)}
            >
              {t('party:party.leave_party')}
            </Button>
          </div>
        </>
      )}
      {isParty && !pending && (
        <>
          <Card className="bg-gradient-primary mt-3">
            <Card.Body>
              <Row>
                <Col>
                  <h5 className="mb-0 text-white">
                    <Trans
                      i18nKey="party.invite_code.title"
                      ns="party"
                      components={{
                        b: <strong />,
                      }}
                      values={{
                        code: partyCode,
                      }}
                    />
                  </h5>
                  <p className="text-white mb-0">{t('party:party.invite_code.introduction')}</p>
                </Col>
                <Col xs="auto" className="d-none d-lg-flex align-items-center">
                  <Button
                    size="sm"
                    rounded
                    icon={<FontAwesomeIcon icon={faCopy} />}
                    variant="white"
                    disabled={!partyCode || copiedToClipboard}
                    onClick={partyCode ? () => toClipboard.mutate(partyCode) : undefined}
                  >
                    {t(
                      copiedToClipboard
                        ? 'party:party.copied_to_clipboard'
                        : 'party:party.copy_code',
                    )}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <div className="d-flex flex-column">
            {party.data?.members.map((member) => (
              <Member
                key={member.vrchatId}
                member={member}
                friendLocation={activeFriends.find((fl) => fl.userId === member.vrchatId)}
                getParty={() => void party.refetch()}
                isOwner={isOwner}
                disabled={party.isRefetching}
              />
            ))}
          </div>
          <div className="text-center">
            <Button
              variant="outline-danger"
              size="sm"
              className="mt-5 mb-3"
              onClick={() => setShowLeavePartyModal(true)}
            >
              {t(isOwner ? 'party:party.delete_party' : 'party:party.leave_party')}
            </Button>
          </div>
        </>
      )}
      <LeavePartyModal
        isOwner={isOwner}
        show={showLeavePartyModal}
        onClose={() => setShowLeavePartyModal(false)}
        onSuccess={() => {
          void party.refetch();
          reset();
        }}
      />
    </div>
  );
};

export const Route = createFileRoute('/party')({
  component: () => (
    <FullLayout title="shared.navigation.party">
      <PartyViewInternal />
    </FullLayout>
  ),
});
